import { ChangeDetectorRef, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { ConfirmationDialogComponent } from 'src/app/components/shared/confirmation-dialog/confirmation-dialog.component';
import { InternalEquipment } from '../../../../model/internalEquipment';
import { firstValueFrom, Subject, takeUntil } from 'rxjs';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { CalibrationBalancePattern, CalibrationBalancePatternValue, CalibrationBalanceUncertaintiesValues, CalibrationBalanceVariable, CalibrationBalanceVariableConfig } from '../../../../model/calibrationBalance';
import { VariableTypeEnum, VariableUnit, VariableUnitEnum } from '../../../../model/variable';
import { CalibrationBalanceService } from '../../../../services/calibrationBalance.service';
import { InternalEquipmentService } from '../../../../services/internalEquipment.service';
import { VariableTypeService } from '../../../../services/variableType.service';
import { TranslateService } from '@ngx-translate/core';
import { SnackBarService } from '../../../../services/snackBar.service';
import { SpinnerService } from '../../../../services/spinner.service';
import { ArrayUtils } from '../../../../utils/arrayUtils';
import { EquipmentAutocompleteFilter } from '../../../../model/autocompleteFilter';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { DateTime } from 'luxon';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { CalibrationBalanceUtils } from '../../../../utils/calibrationBalanceUtils';
import saveAs from 'file-saver';
import { InternalEquipmentBalanceMass } from '../../../../model/internalEquipmentBalanceMass';
import { NumberUtils } from '../../../../utils/numberUtils';
import { ExternalCalibrationValue } from 'src/app/model/calibration';
import { ExternalCalibrationVariable } from '../../../../model/calibration';
import { v4 as uuid } from 'uuid';
import { EquipmentPerformances } from '../../../../utils/equipmentUtils';

export interface DialogDataCalibrationBalancePattern {
  variable: CalibrationBalanceVariable;
  item: CalibrationBalancePattern;
  configs: CalibrationBalanceVariableConfig[];
  units: VariableUnit[];
  unitsExtra: VariableUnit[];
}

@Component({
  selector: 'app-calibration-balance-edit-patterns-dialog',
  templateUrl: './calibration-balance-edit-patterns-dialog.component.html',
  styleUrl: './calibration-balance-edit-patterns-dialog.component.scss',
  standalone: false
})
export class CalibrationBalanceEditPatternsDialogComponent implements OnInit, OnDestroy {

  points: number[] = [];
  public equipmentAutoComplete: InternalEquipment[];
  public equipmentName: string;
  isNotUncertainty = false;
  vBalanceMass: ExternalCalibrationValue[] = [];
  private destroy$ = new Subject<void>();

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: DialogDataCalibrationBalancePattern,
    public dialogRef: MatDialogRef<CalibrationBalanceEditPatternsDialogComponent>,
    private calibrationService: CalibrationBalanceService,
    private internalEquipmentService: InternalEquipmentService,
    private variableTypeService: VariableTypeService,
    private translate: TranslateService,
    public dialog: MatDialog,
    public snackBarService: SnackBarService,
    private spinnerService: SpinnerService,
    private dRef: ChangeDetectorRef) { }

  ngOnInit(): void {
    if (this.data.item.equipment == null) {
      this.data.item.equipment = [];
    }

    if (this.data.item.values == null) {
      this.data.item.values = [];
    }

    if (this.data.configs == null) {
      this.data.configs = [];
    }

    if (!this.data.item.uuidPattern) {
      this.data.item.uuidPattern = uuid();
    }

    this.points = this.data.configs.reduce((acc, aL) => acc.concat(aL.points), []);

    this.data.item.values.forEach(v => {
      v.include = true;
      v.valid = true;
      v.idMass = v.idMass;
      this.dRef.detectChanges();
    });

    const autoinclude = ArrayUtils.isEmpty(this.data.item.values);

    this.data.configs.forEach(p => {

      if (!this.data.item.uuidConfig) {
        this.data.item.uuidConfig = p.uuidConfig;
      }
      p.points?.forEach(po => {
        const point = this.data.item.values.find(va => va.value === Number(po));

        if (point == null) {
          const value = new CalibrationBalancePatternValue();
          value.value = Number(po);
          value.include = autoinclude;
          value.uuidConfig = p.uuidConfig;
          this.data.item.values.push(value);
        }
      })
    });

    if (!ArrayUtils.isEmpty(this.points)) {
      const pointsAsNumber = this.points.map(Number);
      this.data.item.values = this.data.item.values.filter(
        v => pointsAsNumber.includes(v.value)
      );
    }

    this.data.variable.unitName = this.data.units.find(eu => eu.id === this.data.variable.idUnit)?.name;

    this.data.item.values = this.data.item.values.sort((v1, v2) => v1.value - v2.value);

    if (this.data.item.idEquipment?.length > 0) {
      this.data.item.idEquipment.forEach(idF => {
        this.getBalanceMass(idF);
      })
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  lookupEquipment($event): void {
    let results = this.equipmentAutoComplete;

    const filter = new EquipmentAutocompleteFilter();

    filter.query = $event.target.value;
    filter.pageIndex = 0;
    filter.pageSize = 10;
    filter.sortBy = 'name';
    filter.sortDirection = 'asc';
    filter.forBalancePattern = true;

    this.internalEquipmentService.findAutocomplete(filter).pipe(takeUntil(this.destroy$)).subscribe(item => {
      results = item.content;
      if (this.data.item.equipment) {
        if (this.data.item.idEquipment?.length > 0) {
          this.equipmentAutoComplete = results.filter(e => !this.data.item.idEquipment?.includes(e.id));
        } else {
          this.equipmentAutoComplete = results;
        }
      } else {
        this.equipmentAutoComplete = results;
      }
    }, () => {
      results = [];
      this.equipmentAutoComplete = results;
    });
  }

  onSensorChange($event: any, trigger: MatAutocompleteTrigger): void {
    const equipmentSelected = $event.option.value as InternalEquipment;

    const eqCalibration = equipmentSelected.performances?.find(ep => ep.idActionType === EquipmentPerformances.CALIBRATION);
    if (eqCalibration.nearToExpire || eqCalibration.expired) {

      /*this.data.item.equipment.push(equipmentSelected);
      this.data.item.idEquipment.push(equipmentSelected.id);
      this.equipmentName = null;*/

      const expirationDate = DateTime.fromJSDate(eqCalibration.expirationDate).toFormat('dd/LLL/yyyy').replace('./', '/').toUpperCase();

      let message = this.translate.instant('executionEdit.essays.sensor.form.error.nearToExpire', { expirationDate }) as string;

      if (eqCalibration.expired) {
        message = this.translate.instant('executionEdit.essays.sensor.form.error.expired') as string;
      }

      const dialogRef = this.dialog.open(ConfirmationDialogComponent, { data: { message } });

      dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe(result => {
        if (result === true) {
          if (!this.data.item.idEquipment) {
            this.data.item.idEquipment = [];
          }
          this.data.item.equipment.push(equipmentSelected);
          this.data.item.idEquipment.push(equipmentSelected.id);
          this.data.item.idEquipment?.forEach(ec => {
            this.getBalanceMass(ec);
          });
          //this.findUncertaintyFromEquipment(equipmentSelected.id, trigger);
        } else {
          this.data.item.equipment = null;
          this.equipmentName = null;
          this.isNotUncertainty = true;
        }
      });
    } else {
      if (!this.data.item.idEquipment) {
        this.data.item.idEquipment = [];
      }
      this.data.item.equipment.push(equipmentSelected);
      this.data.item.idEquipment.push(equipmentSelected.id);
      this.data.item.idEquipment?.forEach(ec => {
        this.getBalanceMass(ec);
      });
      //this.findUncertaintyFromEquipment(equipmentSelected.id, trigger);
      this.isNotUncertainty = true;
      this.equipmentName = null;
    }
  }

  public setValue(index: number, value: number | string | boolean | MatCheckboxChange, field: string): void {

    if (value == null || value === '') {
      value = null;
    } else if (value instanceof MatCheckboxChange) {
      value = value.checked;
    } else if (typeof value === 'string') {
      value = +value;
    }

    this.data.item.values[index][field] = value;
  }

  displayFn(eq?: InternalEquipment | string | { name: string }): string {
    if (!eq) {
      return null;
    }

    let res: string = null;

    if (typeof eq === 'string') {
      res = eq;
    } else if (eq instanceof InternalEquipment || eq.name != null) {
      res = eq.name;
    }

    return res;
  }

  onOkClick(): void {
    const errs = [];

    this.data.item.values.forEach(v => {
      if (!v.valid) {
        errs.push(`La masa en el punto ${v.value} no corresponde`);
      }
      if (v.include && (v.uncertainty == null || v.uncertainty === 0)) {
        errs.push(`Debe incluir la incertidumbre para el punto ${v.value}`);
      }
    });

    if (errs.length === 0) {
      this.data.item.values = this.data.item.values.filter(v => v.include);
      this.dialogRef.close(this.data.item);
    } else {
      const error = errs.join('\n');
      this.snackBarService.sendError(error);
    }

  }

  onNoClick(): void {
    this.dialogRef.close();
  }

  downloadPdf(id: number, name: string = this.equipmentName): void {
    this.spinnerService.show();

    this.internalEquipmentService.downloadPdf(id).pipe(takeUntil(this.destroy$)).subscribe((res: Blob) => {
      saveAs(res, name + '.pdf');
      this.spinnerService.hide();
    }, error => {
      this.spinnerService.hide();
      console.error(error);
    });
  }

  showExtraField(variable: CalibrationBalanceVariable): boolean {
    return CalibrationBalanceUtils.showExtraField(variable);
  }

  getExtraFieldName(variable: CalibrationBalanceVariable): string {
    return CalibrationBalanceUtils.getExtraFieldName(variable);
  }

  onSumMass(indexMass: number, event: any, itemPattern: CalibrationBalancePattern) {
  const unit = this.data.units.find(eu => eu.id === this.data.variable.idUnit).unit; // unidad de la calibración
  const selectedIds = event.value ?? [];
  const idVariable = this.data.variable.idVariable;

  const resBalanceMass = this.vBalanceMass.filter(bm => selectedIds.includes(bm.id));
  const points: number[] = resBalanceMass.map(bm => bm.point);

  const totalInCalUnit = resBalanceMass
    .map(bm => this.convertToUnit(`${bm.pointUnit}`, unit))
    .reduce((sum, val) => sum + val, 0);

  const pointMass = this.data.item.values[indexMass].value;
  const inputInCalUnit = this.convertToUnit(`${pointMass}${unit}`, unit);

  if (totalInCalUnit === inputInCalUnit) {
    const idUnitTo = this.data.variable.idUnit;
    const someDiffUnit = resBalanceMass.filter(euni => euni.idUnit !== idUnitTo);

    if (someDiffUnit?.length > 0) {
      const units = [...new Set(someDiffUnit.map(du => du.unit))];
      const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
        minWidth: '20%',
        maxHeight: '95vh',
        data: {
          message: this.translate.instant('calibrateEquipmentEdit.uncertainties.form.ask',
            { origin: units.join(', '), destination: this.data.variable.unitName }) as string
        }
      });

      const isTemp = idVariable === VariableTypeEnum.TEMPERATURE;

      dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe(async result => {
        if (result === true) {
          this.spinnerService.show();

          const promises = resBalanceMass.map(async (bm) => {
            const idUnitFrom = bm.idUnit;
            const result = await this.convertPattern(bm, idUnitFrom, idUnitTo, !isTemp);
            this.spinnerService.hide();
            return result;
          });

          const uncertainties = await Promise.all(promises);
          this.setUncertainties(totalInCalUnit, uncertainties, itemPattern.uuidConfig, true);
        } else {
          this.setUncertainties(totalInCalUnit, resBalanceMass, itemPattern.uuidConfig, false);
        }
      });
    } else {
      this.setUncertainties(totalInCalUnit, resBalanceMass, itemPattern.uuidConfig, false);
    }

    this.spinnerService.hide();
    this.data.item.values[indexMass].valid = true;
  } else {
    this.data.item.values[indexMass].valid = false;
    this.data.item.values[indexMass].uncertainty = null;
  }
}


  deleteRowPatternSelected(indexPattern: number) {
    const idEq = this.data.item.idEquipment[indexPattern];
    this.data.item.idEquipment = this.data.item.idEquipment.filter(eid => eid != idEq);
    const eq = this.data.item.equipment.find(eid => eid.id != idEq);
    this.data.item.equipment.splice(indexPattern, 1);
    
    if (this.data.item.idEquipment?.length > 0) {
      this.data.item.idEquipment?.forEach(ec => {
        this.getBalanceMass(ec);
      });
    } else {
      this.vBalanceMass = [];
      this.data.item.values.forEach(e => {
        e.valid = false;
        e.uncertainty = null;
        e.idMass = null;
      })
    }

    /*this.data.item.values = this.data.item.values.filter(eiv =>
      !eiv.idMass.some(eivi =>
        {
          if (eq.) {
            eq.balanceMass?.some(bm => bm.id === eivi);
          } else {
            
          }
        }
      )
    );*/
  }

  getBalanceMass(idEquipment: number) {
    this.vBalanceMass = [];
    this.calibrationService.getMassFromEquipment(idEquipment, this.data.variable.idVariable).pipe(takeUntil(this.destroy$)).subscribe((mass: ExternalCalibrationVariable) => {
      for (let index = 0; index < mass.values.length; index++) {
        const element = mass.values[index];

        element.pointUnit = element.point + mass.unitName;
        element.idUnit = mass.idUnit;
        element.unit = mass.unitName;
        element.uncertaintyOriginal = element.uncertainty;
        element.averageOriginal = element.average;
        this.vBalanceMass.push(element);
      }

      this.vBalanceMass.sort((v1, v2) => v1.id - v2.id);
    });
  }

  getEquipmentName(idEquipment: number) {
    return this.data.item.equipment?.find(e => e.id === idEquipment)?.name;
  }

  getDecimalsUncertainty(uuidConfig: string): number {
    const config = this.findConfig(uuidConfig);

    let res = 2;

    if (config != null) {
      const uncertaintyResolution = config.uncertaintyResolution;
      const uncertaintyDecimals = NumberUtils.countDecimals(uncertaintyResolution);

      if (uncertaintyDecimals === 0) {
        res = uncertaintyResolution;
      } else {
        res = uncertaintyDecimals;
      }
    }

    return res;
  }

  private findConfig(uuidConfig: string): CalibrationBalanceVariableConfig {
    return this.data.configs.find(dc => dc.uuidConfig === uuidConfig);
  }

  private async setUncertainties(point: number, uncertainties: ExternalCalibrationValue[], uuidConfig: string, hasConvert: boolean): Promise<void> {
    if (!ArrayUtils.isEmpty(uncertainties)) {
      uncertainties.forEach(u => {
      if (!u.point && Math.abs(u.point) >= 1e-12) {
        u.point = NumberUtils.fixPrecision(u.point);
      }
      if (!u.uncertainty && Math.abs(u.uncertainty) >= 1e-12) {
        u.uncertainty = NumberUtils.fixPrecision(u.uncertainty);
      }
    });

      const val = this.data.item.values.find(vPoint => vPoint.value  === point);
      if (val) {
        val.include = true;

        const point = val.value;

        const exact = uncertainties.find(u => (hasConvert ? u.pointConvert === point : u.point === point));

        if (exact != null) {
          val.uncertainty = exact.uncertainty;
          val.nominal = exact.average;
          val.decimalPattern = NumberUtils.countDecimalsAny(exact.pattern);
        } else {
          const sumNominal = uncertainties.reduce((acc, eu) => acc + eu.average, 0);

          if (uncertainties.length === 1) {
            const sumUncertainty = uncertainties.reduce((acc, eu) => acc + eu.uncertainty, 0);
            val.uncertainty = Math.sqrt(sumUncertainty);
          } else {
            const totalUncertainty = uncertainties.reduce((acc, eu) => acc + eu.uncertainty ** 2, 0);
            val.uncertainty = Math.sqrt(totalUncertainty);
          }

          val.nominal = sumNominal;
          let maxDecimal = Math.max(...uncertainties.map(eu =>  NumberUtils.countDecimalsAny(eu.pattern)));
          val.decimalPattern = NumberUtils.countDecimalsAny(maxDecimal);
        }
      }
    } else {
      this.showErrorNoUncertainties();
    }
  }

  private async convertPattern(u: ExternalCalibrationValue, idUnitFrom: number, idUnitTo: number, convertUncertainty = true):
    Promise<ExternalCalibrationValue> {

    const variable = this.data.variable;
    const patternDecimals =  NumberUtils.countDecimalsAny(u.pattern);
    let radius = variable.extraField;

    if (idUnitFrom != null && idUnitTo) {
      u.pointConvert = null;
      if (this.showExtraField(variable)) {
        radius = await firstValueFrom(
          this.variableTypeService.convert(variable.extraField, variable.idUnitExtra, VariableUnitEnum.LENGHT_M)) as number;
        u.pointConvert = await firstValueFrom(this.variableTypeService.convertWithRadius(u.point, radius, idUnitFrom, idUnitTo)) as number;
      } else {
        u.pointConvert = await firstValueFrom(this.variableTypeService.convert(u.point, idUnitFrom, idUnitTo)) as number;
      }

      u.uncertainty  = null;
      if (convertUncertainty) {
        if (this.showExtraField(variable)) {
          const uncertainty = await firstValueFrom(
            this.variableTypeService.convertWithRadius(u.uncertaintyOriginal, radius, idUnitFrom, idUnitTo)) as number;
          u.uncertainty = uncertainty;
        } else {
          const uncertainty = await firstValueFrom(this.variableTypeService.convert(u.uncertaintyOriginal, idUnitFrom, idUnitTo)) as number;
          u.uncertainty = uncertainty;
        }
      }

      u.average  = null;
      if (convertUncertainty) {
        if (this.showExtraField(variable)) {
          const average =  await firstValueFrom(
            this.variableTypeService.convertWithRadius(u.averageOriginal, radius, idUnitFrom, idUnitTo)) as number;
          u.average = NumberUtils.toNumberDecimals(average, patternDecimals)
        } else {
          const average = await firstValueFrom(this.variableTypeService.convert(u.averageOriginal, idUnitFrom, idUnitTo)) as number;
          u.average = NumberUtils.toNumberDecimals(average, patternDecimals);
        }
          
      }
    }

    return u;
  }

  private showErrorNoUncertainties() {
    const message = this.translate.instant('calibrateEquipmentEdit.uncertainties.form.noUncertainties') as string;
    const messageCancel = this.translate.instant('button.accept') as string;

    this.dialog.open(ConfirmationDialogComponent, {
      minWidth: '20%',
      data: { message, canOk: false, messageCancel }
    });

    this.data.item.equipment = null;
    this.equipmentName = null;

    /*this.data.item.values.forEach(val => {
      val.uncertainty = null;
    });*/
  }

  private showErrorNotCalibrated(pointsErrs: number[], minPoint: number, maxPoint: number): void {
    const points = pointsErrs.join(', ');

    const message = this.translate.instant('calibrateEquipmentEdit.uncertainties.form.patternNotValid',
      { points, minPoint, maxPoint }) as string;
    const messageCancel = this.translate.instant('button.accept') as string;

    this.dialog.open(ConfirmationDialogComponent, {
      minWidth: '20%',
      data: { message, canOk: false, messageCancel }
    });

  }

  private convertToGram(valor: string): number {
    const num = parseFloat(valor);
    const unidad = valor.replace(/^[0-9.\s]+/, '').replace(/[^a-zA-Z%]+/g, '');
    switch (unidad) {
      case 'mg': return num / 1000;
      case 'g': return num;
      case 'kg': return num * 1000;
      default: throw new Error(`Unidad desconocida: ${unidad}`);
    }
  }

  private convertToUnit(valor: string, targetUnit: string): number {
    const num = parseFloat(valor);
    if (isNaN(num)) {
      throw new Error(`Valor numérico no válido: "${valor}"`);
    }

    const unidad = valor.replace(/^[0-9.\s]+/, '').trim().toLowerCase();

    let valueInGrams: number;
    switch (unidad) {
      case 'mg':
        valueInGrams = num / 1000;
        break;
      case 'g':
        valueInGrams = num;
        break;
      case 'kg':
        valueInGrams = num * 1000;
        break;
      default:
        throw new Error(`Unidad desconocida: "${unidad}"`);
    }

    switch (targetUnit.toLowerCase()) {
      case 'mg':
        return valueInGrams * 1000;
      case 'g':
        return valueInGrams;
      case 'kg':
        return valueInGrams / 1000;
      default:
        throw new Error(`Unidad de calibración desconocida: "${targetUnit}"`);
    }
  }

}
